import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { api, fileUrl } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Eye, Check, X, Sparkles, Search, BarChart3, MessageCircle, TrendingUp, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [angels, setAngels] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [seo, setSeo] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    const settle = async (p) => { try { return await p; } catch (e) { return { data: null }; } };
    const [a, r, v, s, an] = await Promise.all([
      settle(api.get("/admin/angels")),
      settle(api.get("/admin/receipts")),
      settle(api.get("/admin/reviews", { params: { status: "pending" } })),
      settle(api.get("/settings")),
      settle(api.get("/admin/analytics", { params: { days: 7 } })),
    ]);
    setAngels(a.data || []);
    setReceipts(r.data || []);
    setReviews(v.data || []);
    setSeo(s.data || null);
    setAnalytics(an.data || null);
  };
  useEffect(() => { load(); }, []);

  const updateAngel = async (id, patch) => {
    const r = await api.put(`/admin/angels/${id}`, patch);
    setAngels((arr) => arr.map((a) => a.angel_id === id ? { ...a, ...r.data } : a));
    toast.success("Actualizado");
  };

  const deleteAngel = async (id, name) => {
    if (!window.confirm(`¿Eliminar definitivamente el perfil de ${name}? Esta acción borra fotos, muro, reseñas y favoritos. No se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/angels/${id}`);
      setAngels((arr) => arr.filter((a) => a.angel_id !== id));
      toast.success("Perfil eliminado");
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const decideReceipt = async (id, status) => {
    await api.put(`/admin/receipts/${id}`, { status });
    toast.success("Comprobante actualizado");
    load();
  };
  const decideReview = async (id, status) => {
    await api.put(`/admin/reviews/${id}`, { status });
    toast.success("Reseña actualizada");
    load();
  };

  const seedDemo = async () => {
    await api.post("/admin/seed-demo");
    toast.success("Datos demo creados");
    load();
  };

  const saveSeo = async () => {
    try {
      const r = await api.put("/admin/settings", seo);
      setSeo(r.data);
      toast.success("SEO actualizado");
    } catch (e) { toast.error("Error guardando SEO"); }
  };

  const runExpiryNow = async () => {
    if (!window.confirm("Forzar el chequeo de vencimientos ahora? Ocultará los perfiles cuyo plan haya expirado.")) return;
    try {
      const r = await api.post("/admin/run-expire");
      toast.success(`Vencimientos aplicados: ${r.data.expired} perfil(es) ocultado(s)`);
      load();
    } catch (e) { toast.error("No se pudo ejecutar"); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#6B6A63]">Panel Administrador</div>
            <h1 className="font-serif text-4xl">Lista Maestra</h1>
          </div>
          <button data-testid="run-expiry" onClick={runExpiryNow} className="btn-ghost text-sm inline-flex items-center gap-1">
            <TrendingUp size={16} /> Aplicar vencimientos
          </button>
          <button data-testid="seed-demo" onClick={seedDemo} className="btn-ghost text-sm inline-flex items-center gap-1">
            <Sparkles size={16} /> Crear demo
          </button>
        </div>

        <Tabs defaultValue="master">
          <TabsList className="bg-[#F3F1EA]">
            <TabsTrigger data-testid="tab-master" value="master">Modelos ({angels.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-receipts" value="receipts">Comprobantes ({receipts.filter(r=>r.status==='pending').length})</TabsTrigger>
            <TabsTrigger data-testid="tab-reviews" value="reviews">Reseñas ({reviews.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-analytics" value="analytics">Analítica</TabsTrigger>
            <TabsTrigger data-testid="tab-seo" value="seo">SEO</TabsTrigger>
          </TabsList>

          <TabsContent value="master" className="pt-6">
            <div className="bg-white border border-[#E5E2D9] rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-widest text-[#6B6A63] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-3 py-3">Modelo</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">Categoría</th>
                    <th className="px-3 py-3">Vence</th>
                    <th className="px-3 py-3">Verificada</th>
                    <th className="px-3 py-3">Publicado</th>
                    <th className="px-3 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {angels.map((a) => (
                    <tr key={a.angel_id} className="border-b border-[#E5E2D9] last:border-b-0" data-testid={`row-${a.angel_id}`}>
                      <td className="px-3 py-3 flex items-center gap-2">
                        {a.main_photo_path ? (
                          <img src={fileUrl(a.main_photo_path)} alt="" className="w-10 h-12 object-cover rounded" />
                        ) : <div className="w-10 h-12 bg-[#F3F1EA] rounded" />}
                        <div>
                          <div className="font-medium">{a.artistic_name}</div>
                          <div className="text-xs text-[#6B6A63]">{a.age ? `${a.age} años` : ""} {a.comuna ? `· ${a.comuna}` : ""}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#6B6A63]">{a.email || "—"}</td>
                      <td className="px-3 py-3">
                        <Select value={a.tier} onValueChange={(v) => updateAngel(a.angel_id, { tier: v })}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="premium">Premium</SelectItem>
                            <SelectItem value="gold">Gold</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3 text-xs">{a.expires_at ? new Date(a.expires_at).toLocaleDateString("es-CL") : "—"}</td>
                      <td className="px-3 py-3">
                        <Switch checked={!!a.verified} onCheckedChange={(v) => updateAngel(a.angel_id, { verified: v })} />
                      </td>
                      <td className="px-3 py-3">
                        <Switch
                          data-testid={`pub-${a.angel_id}`}
                          checked={!!a.is_published}
                          onCheckedChange={(v) => updateAngel(a.angel_id, { is_published: v })}
                        />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          data-testid={`delete-${a.angel_id}`}
                          onClick={() => deleteAngel(a.angel_id, a.artistic_name)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-red-600 hover:bg-red-50 transition"
                          aria-label="Eliminar perfil"
                          title="Eliminar perfil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!angels.length && (
                    <tr><td colSpan="7" className="px-3 py-6 text-center text-[#6B6A63]">Sin modelos aún.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="receipts" className="pt-6">
            <div className="bg-white border border-[#E5E2D9] rounded-xl divide-y divide-[#E5E2D9]">
              {receipts.map((r) => (
                <div key={r.receipt_id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3">
                  <div className="flex-1">
                    <div className="text-sm">Plan: <strong>{r.tier_requested}</strong> · {new Date(r.created_at).toLocaleString("es-CL")}</div>
                    <div className="text-xs text-[#6B6A63]">Estado: {r.status}</div>
                  </div>
                  <button onClick={() => setViewing(r.path)} className="btn-ghost text-sm inline-flex items-center gap-1"><Eye size={14}/> Ver</button>
                  <button onClick={() => decideReceipt(r.receipt_id, "approved")} className="btn-primary text-sm inline-flex items-center gap-1"><Check size={14}/> Aprobar</button>
                  <button onClick={() => decideReceipt(r.receipt_id, "rejected")} className="btn-ghost text-sm inline-flex items-center gap-1"><X size={14}/> Rechazar</button>
                </div>
              ))}
              {!receipts.length && <div className="px-4 py-6 text-sm text-[#6B6A63] text-center">Sin comprobantes.</div>}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="pt-6">
            <div className="bg-white border border-[#E5E2D9] rounded-xl divide-y divide-[#E5E2D9]">
              {reviews.map((r) => (
                <div key={r.review_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1">
                    <div className="text-xs text-[#6B6A63]">{r.user_name} · {new Date(r.created_at).toLocaleString("es-CL")}</div>
                    <div className="text-sm">{r.comment}</div>
                    <div className="text-xs text-[#6B6A63] mt-1">{Object.entries(r.ratings||{}).map(([k,v])=>`${k}:${v}`).join(" · ")}</div>
                  </div>
                  <button onClick={() => decideReview(r.review_id, "approved")} className="btn-primary text-sm inline-flex items-center gap-1"><Check size={14}/> Aprobar</button>
                  <button onClick={() => decideReview(r.review_id, "rejected")} className="btn-ghost text-sm inline-flex items-center gap-1"><X size={14}/> Rechazar</button>
                </div>
              ))}
              {!reviews.length && <div className="px-4 py-6 text-sm text-[#6B6A63] text-center">Sin reseñas pendientes.</div>}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="pt-6">
            {analytics && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white border border-[#E5E2D9] rounded-xl p-4">
                    <div className="text-xs uppercase tracking-widest text-[#6B6A63] flex items-center gap-2"><Eye size={14}/> Vistas totales</div>
                    <div className="font-serif text-3xl mt-1">{analytics.totals.views.toLocaleString("es-CL")}</div>
                  </div>
                  <div className="bg-white border border-[#E5E2D9] rounded-xl p-4">
                    <div className="text-xs uppercase tracking-widest text-[#6B6A63] flex items-center gap-2"><MessageCircle size={14}/> Clicks a WhatsApp</div>
                    <div className="font-serif text-3xl mt-1">{analytics.totals.wa_clicks.toLocaleString("es-CL")}</div>
                  </div>
                  <div className="bg-white border border-[#E5E2D9] rounded-xl p-4">
                    <div className="text-xs uppercase tracking-widest text-[#6B6A63] flex items-center gap-2"><TrendingUp size={14}/> Conversión</div>
                    <div className="font-serif text-3xl mt-1">{analytics.totals.conversion}%</div>
                  </div>
                </div>

                <div className="bg-white border border-[#E5E2D9] rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-widest text-[#6B6A63] border-b border-[#E5E2D9]">
                      <tr>
                        <th className="px-3 py-3">Modelo</th>
                        <th className="px-3 py-3">Categoría</th>
                        <th className="px-3 py-3 text-right">Vistas (total)</th>
                        <th className="px-3 py-3 text-right">Vistas (7d)</th>
                        <th className="px-3 py-3 text-right">Clicks WA</th>
                        <th className="px-3 py-3 text-right">Clicks WA (7d)</th>
                        <th className="px-3 py-3 text-right">Conv.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.rows.map((r) => (
                        <tr key={r.angel_id} className="border-b border-[#E5E2D9] last:border-b-0" data-testid={`an-${r.angel_id}`}>
                          <td className="px-3 py-3 flex items-center gap-2">
                            {r.main_photo_path ? (
                              <img src={fileUrl(r.main_photo_path)} alt="" className="w-9 h-12 object-cover rounded no-save" draggable={false} onContextMenu={(e)=>e.preventDefault()} />
                            ) : <div className="w-9 h-12 bg-[#F3F1EA] rounded" />}
                            <div>
                              <div className="font-medium">{r.artistic_name}</div>
                              <div className="text-xs text-[#6B6A63]">{r.is_published ? "Publicada" : "Oculta"}</div>
                            </div>
                          </td>
                          <td className="px-3 py-3 capitalize">{r.tier}</td>
                          <td className="px-3 py-3 text-right">{r.views_total}</td>
                          <td className="px-3 py-3 text-right text-[#364531] font-medium">{r.views_recent}</td>
                          <td className="px-3 py-3 text-right">{r.wa_clicks_total}</td>
                          <td className="px-3 py-3 text-right text-[#364531] font-medium">{r.wa_clicks_recent}</td>
                          <td className="px-3 py-3 text-right">{r.conversion}%</td>
                        </tr>
                      ))}
                      {!analytics.rows.length && (
                        <tr><td colSpan="7" className="px-3 py-6 text-center text-[#6B6A63]">Sin datos aún.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-[#6B6A63]">Las vistas se cuentan únicas por IP por día. Los clicks a WhatsApp se contabilizan al pulsar el botón en el perfil de la modelo.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="seo" className="pt-6">
            <div className="bg-white border border-[#E5E2D9] rounded-xl p-5 space-y-4 max-w-3xl">
              <div className="flex items-center gap-2">
                <Search size={18} className="text-[#364531]" />
                <div className="font-serif text-2xl">Editor SEO</div>
              </div>
              <p className="text-xs text-[#6B6A63]">Esta información se aplica al título del navegador y a las metaetiquetas de la página principal.</p>

              {seo && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Título de la página</Label>
                    <Input data-testid="seo-title" value={seo.title || ""} onChange={(e) => setSeo((s) => ({ ...s, title: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Meta descripción</Label>
                    <Textarea data-testid="seo-desc" value={seo.description || ""} onChange={(e) => setSeo((s) => ({ ...s, description: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Palabras clave (separadas por coma)</Label>
                    <Input data-testid="seo-keywords" value={seo.keywords || ""} onChange={(e) => setSeo((s) => ({ ...s, keywords: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Open Graph image URL</Label>
                    <Input data-testid="seo-og" value={seo.og_image || ""} onChange={(e) => setSeo((s) => ({ ...s, og_image: e.target.value }))} placeholder="https://…" />
                  </div>
                  <div>
                    <Label className="text-xs">Nombre del sitio</Label>
                    <Input data-testid="seo-site" value={seo.site_name || ""} onChange={(e) => setSeo((s) => ({ ...s, site_name: e.target.value }))} />
                  </div>

                  <div className="pt-2 border-t border-[#E5E2D9]">
                    <div className="text-sm font-medium mb-2">Datos bancarios (mostrados a modelos)</div>
                    <Textarea data-testid="seo-bank" value={seo.bank_info || ""} onChange={(e) => setSeo((s) => ({ ...s, bank_info: e.target.value }))} />
                  </div>

                  <div className="pt-2 border-t border-[#E5E2D9]">
                    <Label className="text-xs">Política de Privacidad</Label>
                    <Textarea data-testid="seo-privacy" rows={8}
                              value={seo.privacy_text || ""}
                              onChange={(e) => setSeo((s) => ({ ...s, privacy_text: e.target.value }))} />
                  </div>

                  <div>
                    <Label className="text-xs">Términos y Condiciones</Label>
                    <Textarea data-testid="seo-terms" rows={10}
                              value={seo.terms_text || ""}
                              onChange={(e) => setSeo((s) => ({ ...s, terms_text: e.target.value }))} />
                  </div>

                  <button data-testid="seo-save" onClick={saveSeo} className="btn-primary">Guardar SEO</button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          {viewing && (viewing.endsWith(".pdf") ? (
            <iframe title="receipt" src={fileUrl(viewing)} className="w-full h-[80vh]" />
          ) : (
            <img src={fileUrl(viewing)} alt="Comprobante" className="w-full max-h-[85vh] object-contain" />
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
